/**
 * GhostShiftPreview - Visual preview component for new shift creation
 * Shows a semi-transparent "ghost" block while user is dragging to create a shift
 */
import { useCalendarEdit } from "./CalendarEditContext";
import { cn } from "../../lib/utils";
import { Plus } from "lucide-react";

const HOUR_HEIGHT = 60; // pixels per hour (must match calendar)
const FIRST_HOUR = 6; // Calendar starts at 6 AM

/**
 * Convert time string (HH:MM) to pixel position
 */
const timeToPixel = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return (hours - FIRST_HOUR) * HOUR_HEIGHT + (minutes / 60) * HOUR_HEIGHT;
};

export function GhostShiftPreview({ containerTop = 0 }) {
  const { isCreatingShift, newShiftPreview } = useCalendarEdit();
  
  if (!isCreatingShift || !newShiftPreview) {
    return null;
  }
  
  const { startY, currentY } = newShiftPreview;
  
  // Calculate top and height from Y positions
  const relativeStartY = startY - containerTop;
  const relativeCurrentY = currentY - containerTop;
  
  const top = Math.min(relativeStartY, relativeCurrentY);
  const height = Math.abs(relativeCurrentY - relativeStartY);
  
  // Minimum height of 15 minutes (15px)
  const displayHeight = Math.max(height, 15);
  
  return (
    <div
      className={cn(
        "absolute left-1 right-1 rounded-md border-2 border-dashed",
        "bg-primary/20 border-primary",
        "flex items-center justify-center",
        "pointer-events-none",
        "transition-opacity duration-100"
      )}
      style={{
        top: `${Math.max(top, 0)}px`,
        height: `${displayHeight}px`,
      }}
      data-testid="ghost-shift-preview"
    >
      <div className="flex items-center gap-1 text-primary text-xs font-medium">
        <Plus className="h-3.5 w-3.5" />
        <span>New Shift</span>
      </div>
    </div>
  );
}

export default GhostShiftPreview;
